import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Signal } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { BaseWebsocketMessage } from '@qtek/core/websockets-core';
import {
  ErrorSnackBarData,
  NormalSnackBarData,
  SnackBarService,
} from '@qtek/libs/snack-bar-core';
import {
  BatchCopyWorkflowsRequest,
  EmailDynamicContent,
  EmailTemplate,
  PublishWorkflowResponse,
  RoleParticipantsData,
  TriggerPayload,
  Workflow,
  WorkflowDetails,
  WorkflowHistoryActivity,
  WorkflowPayload,
  WorkflowRun,
  WorkflowTaskPayload,
} from '@qtek/shared/models';
import { EMPTY, merge, of, pipe, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  filter,
  finalize,
  groupBy,
  map,
  mergeMap,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { WorkflowApiService, WorkflowEntity } from './workflow-api.service';
import { WorkflowFlowableService } from './workflow-flowable.service';
import {
  WorkflowRunService,
  WorkflowRunWsMessage,
} from './workflow-run.service';
import {
  mergeActiveWorkflow,
  removeWorkflowRow,
  upsertWorkflowRow,
  upsertWorkflowRowTrigger,
  upsertWorkflowRun,
} from './workflow.updaters';

export interface WorkflowStoreState {
  workflowsByItem: Record<string, Workflow[]>;
  loadingByItem: Record<string, boolean>;
  activeWorkflow: WorkflowDetails | null;
  activeLoading: boolean;
  myDealsWorkflows: Workflow[];
  myDealsLoading: boolean;
  copyLoading: boolean;
  roleParticipantsByItem: Record<string, RoleParticipantsData>;
  emailDynamicContent: EmailDynamicContent | null;
  emailTemplates: EmailTemplate[];
  workflowRuns: Record<string, WorkflowRun[]>;
  workflowRunsLoading: Record<string, boolean>;
  workflowHistory: Record<string, WorkflowHistoryActivity[] | null>;
  workflowHistoryLoading: Record<string, boolean>;
  error: Error | null;
}

export const listMysid = (itemId: string) => `workflows-${itemId}`;
export const detailsMysid = (workflowId: string) => `workflow-${workflowId}`;
export const MY_DEALS_MYSID = 'workflows-my-deals';

const DETAILS_ENTITIES: WorkflowEntity[] = [
  'Workflow',
  'WorkflowTaskConfiguration',
  'Trigger',
];

interface WorkflowWsMessage extends BaseWebsocketMessage {
  id?: string;
  mysid?: string;
  // `res` is a row, a row list or absent depending on op; narrowed per case.
  res?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  sts?: { sts?: string };
}

type AckOp = 'ins' | 'upd' | 'upds' | 'del';

// An ack that never arrives (dropped socket) must not make later broadcasts look like mine.
const ACK_TTL_MS = 30_000;

interface PendingAck {
  at: number;
  // Acks expected for one send (a swap may be acked as `upds` or as two `upd`s).
  group: object;
}

// One per open workflow. It outlives closeWorkflow while acks are outstanding, so
// an edit made just before leaving the builder can still be published.
interface DetailsSession {
  id: string;
  close$: Subject<void>;
  changed$: Subject<void>;
  pending: Map<string, PendingAck[]>;
  publishDue: boolean;
  // The details were just refetched (own create ack); the debounced refetch can skip its fetch.
  refetched: boolean;
  // Snapshot of the workflow's `active` flag once the session is no longer open.
  active: boolean | undefined;
}

// Inserts have no id yet, so they are matched by entity and op alone.
const ackKey = (ent: WorkflowEntity, op: AckOp, id?: string) =>
  `${ent}:${op}:${op === 'upd' || op === 'del' ? (id ?? '') : ''}`;

// [failed key, success key]; no success snackbar for trigger updates or swaps.
const ACK_KEYS: Record<
  WorkflowEntity,
  Partial<Record<AckOp, [string, string?]>>
> = {
  Workflow: {
    upd: ['GNR_WORKFLOW_UPDATE_FAILED', 'GNR_WORKFLOW_UPDATED_SUCCESSFULLY'],
    del: ['GNR_WORKFLOW_DELETE_FAILED', 'GNR_WORKFLOW_DELETED_SUCCESSFULLY'],
  },
  WorkflowTaskConfiguration: {
    ins: ['GNR_TASK_CREATE_FAILED', 'GNR_TASK_CREATED_SUCCESSFULLY'],
    upd: ['GNR_TASK_UPDATE_FAILED', 'GNR_TASK_UPDATED_SUCCESSFULLY'],
    upds: ['GNR_TASK_UPDATE_FAILED'],
    del: ['GNR_TASK_DELETE_FAILED', 'GNR_TASK_DELETED_SUCCESSFULLY'],
  },
  Trigger: {
    ins: ['GNR_TRIGGER_CREATE_FAILED', 'GNR_TRIGGER_CREATED_SUCCESSFULLY'],
    upd: ['GNR_TRIGGER_UPDATE_FAILED'],
    del: ['GNR_TRIGGER_DELETE_FAILED', 'GNR_TRIGGER_DELETED_SUCCESSFULLY'],
  },
};

const EMPTY_ROLE_PARTICIPANTS: RoleParticipantsData = {
  internalParticipants: {},
  externalParticipants: {},
  allParticipants: {},
};

const isMissingParticipation = (err: HttpErrorResponse) =>
  Object.values<{ msg?: string }>(err?.error?.sts?.attrs ?? {}).some(
    a => a?.msg === 'ERR_USER_PARTICIPATION_IN_DEAL_NOT_FOUND'
  );

const isKnownRow = (
  active: WorkflowDetails | null,
  ent: WorkflowEntity,
  id: string
): boolean => {
  if (!active) return false;
  if (ent === 'Trigger') return !!active.triggers?.some(t => t._id === id);
  const inTasks = (tasks?: { _id: string }[]) =>
    !!tasks?.some(t => t._id === id);
  return (
    inTasks(active.workflowTasks) ||
    !!active.subWorkflows?.some(sw => inTasks(sw.workflowTasks))
  );
};

const isWsError = (m: WorkflowWsMessage) =>
  m.sts?.sts === 'ERR' || m.sts?.sts === 'WARN';

const isRunWsError = (m: WorkflowRunWsMessage) =>
  m.sts?.sts === 'ERR' || m.sts?.sts === 'WARN';

const initialState: WorkflowStoreState = {
  workflowsByItem: {},
  loadingByItem: {},
  activeWorkflow: null,
  activeLoading: false,
  myDealsWorkflows: [],
  myDealsLoading: false,
  copyLoading: false,
  roleParticipantsByItem: {},
  emailDynamicContent: null,
  emailTemplates: [],
  workflowRuns: {},
  workflowRunsLoading: {},
  workflowHistory: {},
  workflowHistoryLoading: {},
  error: null,
};

export const WorkflowStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withProps(() => ({
    workflowCreated$: new Subject<Workflow>(),
    workflowsCopied$: new Subject<void>(),
  })),
  withComputed(({ myDealsWorkflows }) => ({
    // Only workflows with tasks are copy-eligible; the backend rejects empty ones.
    myDealsWorkflowsGrouped: computed(() => {
      const groups = new Map<string, Workflow[]>();
      myDealsWorkflows()
        .filter(w => w.hasTasks === true && !w.parentWorkflow)
        .forEach(w => {
          const key = w.dealName || '';
          groups.set(key, [...(groups.get(key) ?? []), w]);
        });
      return Array.from(groups, ([dealName, workflows]) => ({
        dealName,
        workflows,
      }));
    }),
  })),
  withMethods(
    (
      store,
      workflowService = inject(WorkflowApiService),
      workflowFlowableService = inject(WorkflowFlowableService),
      runService = inject(WorkflowRunService),
      snackBar = inject(SnackBarService)
    ) => {
      const connected = new Map<string, number>();
      const disconnect$ = new Subject<string>();
      const closeMyDeals$ = new Subject<void>();
      let myDealsCount = 0;
      const runsCount = new Map<string, number>();
      const historyCount = new Map<string, number>();
      const disconnectRuns$ = new Subject<string>();
      const disconnectHistory$ = new Subject<string>();
      const pendingCreates = new Map<string, string[]>();
      const forItemCache = new Map<string, Signal<Workflow[]>>();
      const pendingListDeletes = new Set<string>();
      let session: DetailsSession | null = null;
      // Closed sessions still waiting for acks; swept whenever a workflow opens or closes.
      const draining = new Set<DetailsSession>();
      // Create toasts wait for the refetched details so they land with the new row.
      let pendingCreateToasts: string[] = [];

      const fail = (message: string, error?: unknown) => {
        patchState(store, {
          error: error instanceof Error ? error : new Error(message),
        });
        snackBar.showSnackBar({
          type: 'ErrorMessage',
          message,
        } as ErrorSnackBarData);
      };
      const ok = (message: string) =>
        snackBar.showSnackBar({
          type: 'NormalMessage',
          message,
        } as NormalSnackBarData);
      const takePending = (
        pending: Map<string, string[]>,
        key: string,
        name?: string
      ): boolean => {
        const names = pending.get(key) ?? [];
        const index = name ? names.indexOf(name) : -1;
        if (index < 0) return false;
        pending.set(
          key,
          names.filter((_, i) => i !== index)
        );
        return true;
      };

      const sweepAcks = (s: DetailsSession) => {
        const cutoff = Date.now() - ACK_TTL_MS;
        s.pending.forEach((list, key) => {
          const fresh = list.filter(a => a.at > cutoff);
          if (fresh.length) s.pending.set(key, fresh);
          else s.pending.delete(key);
        });
      };
      const hasPendingAcks = (s: DetailsSession) => {
        sweepAcks(s);
        return s.pending.size > 0;
      };
      const expectAcks = (s: DetailsSession, keys: string[]) => {
        sweepAcks(s);
        const ack = { at: Date.now(), group: {} };
        keys.forEach(key =>
          s.pending.set(key, [...(s.pending.get(key) ?? []), ack])
        );
      };
      // Acks and other sessions' broadcasts share the mysid; match own sends by entity, op and id.
      const takeAck = (s: DetailsSession, key: string): boolean => {
        sweepAcks(s);
        const first = s.pending.get(key)?.[0];
        if (!first) return false;
        s.pending.forEach((list, k) => {
          const rest = list.filter(a => a.group !== first.group);
          if (rest.length) s.pending.set(k, rest);
          else s.pending.delete(k);
        });
        return true;
      };
      const isActive = (s: DetailsSession) =>
        s === session ? store.activeWorkflow()?.active : s.active;

      const onListMessage = (itemId: string, m: WorkflowWsMessage) => {
        switch (m.op) {
          case 'query': {
            if (isWsError(m)) {
              // A failed load must not replace what is already loaded.
              patchState(store, state => ({
                loadingByItem: { ...state.loadingByItem, [itemId]: false },
                error: new Error('GNR_WORKFLOW_LOAD_FAILED'),
              }));
              return;
            }
            const rows: Workflow[] = m.res ?? [];
            patchState(store, state => ({
              workflowsByItem: {
                ...state.workflowsByItem,
                [itemId]: rows.filter(
                  w => w.item === itemId && !w.parentWorkflow
                ),
              },
              loadingByItem: { ...state.loadingByItem, [itemId]: false },
            }));
            break;
          }
          case 'ins': {
            if (isWsError(m) || !m.res?._id) {
              if (
                takePending(
                  pendingCreates,
                  itemId,
                  (pendingCreates.get(itemId) ?? [])[0]
                )
              )
                fail('GNR_WORKFLOW_CREATE_FAILED');
              return;
            }
            patchState(store, state => ({
              workflowsByItem: upsertWorkflowRow(
                state.workflowsByItem,
                itemId,
                m.res,
                'ins'
              ),
            }));
            if (takePending(pendingCreates, itemId, m.res.name)) {
              ok('GNR_WORKFLOW_CREATED_SUCCESSFULLY');
              store.workflowCreated$.next(m.res);
            }
            break;
          }
          case 'upd':
            if (m.res?._id) {
              patchState(store, state => ({
                workflowsByItem: upsertWorkflowRow(
                  state.workflowsByItem,
                  itemId,
                  m.res,
                  'upd'
                ),
                activeWorkflow: mergeActiveWorkflow(
                  state.activeWorkflow,
                  m.res
                ),
              }));
            }
            break;
          case 'del': {
            const id = m.res?._id ?? m.id;
            if (isWsError(m)) {
              if (id) pendingListDeletes.delete(id);
              fail('GNR_WORKFLOW_DELETE_FAILED');
              return;
            }
            if (!id) return;
            patchState(store, state => ({
              workflowsByItem: removeWorkflowRow(state.workflowsByItem, id),
            }));
            if (pendingListDeletes.delete(id))
              ok('GNR_WORKFLOW_DELETED_SUCCESSFULLY');
            break;
          }
        }
      };

      const publishIfDue = (s: DetailsSession) => {
        if (s.publishDue && isActive(s)) {
          s.publishDue = false;
          publish(s.id);
        }
      };

      // A closed session keeps listening only until its outstanding acks are in.
      const settle = (s: DetailsSession) => {
        if (s === session) return;
        publishIfDue(s);
        if (!hasPendingAcks(s)) {
          draining.delete(s);
          s.close$.next();
        }
      };

      const onDetailsMessage = (
        s: DetailsSession,
        ent: WorkflowEntity,
        m: WorkflowWsMessage
      ) => {
        const workflowId = s.id;
        if (ent === 'Workflow' && m.op === 'get') {
          if (s !== session) return;
          if (isWsError(m)) {
            patchState(store, {
              activeLoading: false,
              error: new Error('GNR_WORKFLOW_LOAD_FAILED'),
            });
            return;
          }
          patchState(store, { activeWorkflow: m.res, activeLoading: false });
          pendingCreateToasts.forEach(ok);
          pendingCreateToasts = [];
          return;
        }
        const keys = ACK_KEYS[ent][m.op as AckOp];
        if (!keys) return;
        // Pushes are not scoped to the subscription: drop other workflows' tasks and triggers.
        const pushWorkflow = m.res?.workflow;
        if (
          ent !== 'Workflow' &&
          typeof pushWorkflow === 'string' &&
          pushWorkflow !== workflowId
        )
          return;
        const rowId: string | undefined = m.id ?? m.res?._id;
        const own = takeAck(s, ackKey(ent, m.op as AckOp, rowId));
        if (isWsError(m)) {
          fail(keys[0]);
          settle(s);
          return;
        }
        // Without a workflow on the push, only own acks and rows already shown are ours.
        if (
          ent !== 'Workflow' &&
          !own &&
          pushWorkflow === undefined &&
          !(
            m.op !== 'ins' &&
            !!rowId &&
            isKnownRow(store.activeWorkflow(), ent, rowId)
          )
        )
          return;
        if (ent === 'Trigger') {
          if (rowId) {
            patchState(store, state => ({
              workflowsByItem: upsertWorkflowRowTrigger(
                state.workflowsByItem,
                workflowId,
                { ...m.res, _id: rowId },
                m.op as 'ins' | 'upd' | 'del'
              ),
            }));
          }
        }
        let refetchedNow = false;
        if (own) {
          s.publishDue = true;
          if (m.op === 'ins' && keys[1] && s === session) {
            pendingCreateToasts.push(keys[1]);
            loadDetails(workflowId);
            refetchedNow = true;
          } else if (keys[1]) ok(keys[1]);
        }
        if (ent === 'Workflow' && m.op === 'upd' && m.res?._id) {
          if (m.res._id === workflowId && m.res.active !== undefined)
            s.active = m.res.active;
          patchState(store, state => ({
            activeWorkflow: mergeActiveWorkflow(state.activeWorkflow, m.res),
          }));
        }
        if (s === session) {
          s.refetched = refetchedNow;
          s.changed$.next();
        } else settle(s);
      };

      const onRunsMessage = (workflowId: string, m: WorkflowRunWsMessage) => {
        if (m.op === 'query') {
          patchState(store, state => ({
            workflowRuns: isRunWsError(m)
              ? state.workflowRuns
              : {
                  ...state.workflowRuns,
                  // The API sends newest first; runs are numbered oldest first.
                  [workflowId]: [...((m.res as WorkflowRun[]) ?? [])].reverse(),
                },
            workflowRunsLoading: {
              ...state.workflowRunsLoading,
              [workflowId]: false,
            },
          }));
          return;
        }
        const run = m.res as WorkflowRun | undefined;
        // Pushes are not reliably scoped to the subscription's prms.
        if (
          (m.op === 'ins' || m.op === 'upd') &&
          run?.workflowId === workflowId
        ) {
          patchState(store, state => ({
            workflowRuns: {
              ...state.workflowRuns,
              [workflowId]: upsertWorkflowRun(
                state.workflowRuns[workflowId] ?? [],
                run
              ),
            },
          }));
        }
      };

      const onHistoryMessage = (runId: string, m: WorkflowRunWsMessage) => {
        const run = m.res as WorkflowRun | undefined;
        // A missing or inaccessible run comes back as a get with no res.
        if (m.op === 'get' && (isRunWsError(m) || !run)) {
          patchState(store, state => ({
            workflowHistoryLoading: {
              ...state.workflowHistoryLoading,
              [runId]: false,
            },
          }));
          return;
        }
        if (
          (m.op === 'get' || m.op === 'upd') &&
          run?._id === runId &&
          run.history
        ) {
          patchState(store, state => ({
            workflowHistory: {
              ...state.workflowHistory,
              [runId]: run.history ?? null,
            },
            workflowHistoryLoading: {
              ...state.workflowHistoryLoading,
              [runId]: false,
            },
          }));
        }
      };

      const patchPublished = (id: string, r: PublishWorkflowResponse) =>
        patchState(store, state => ({
          activeWorkflow: mergeActiveWorkflow(state.activeWorkflow, {
            _id: id,
            flowableProcessDefinitionKey: r.processDefinitionKey,
            flowableProcessDefinitionVersion: r.processDefinitionVersion,
          }),
        }));

      const publish = (id: string): void => {
        workflowFlowableService.publish(id).subscribe({
          next: r => patchPublished(id, r),
          error: error => fail('GNR_WORKFLOW_PUBLISH_FAILED', error),
        });
      };

      const sendOnDetails = (
        keys: string[],
        send: (mysid: string) => void
      ): void => {
        // No open session: nothing to send on. Not reported as an error because a
        // late edit racing closeWorkflow (e.g. a blur during teardown) is a normal flow.
        if (!session) return;
        expectAcks(session, keys);
        send(detailsMysid(session.id));
      };

      const updateWorkflow = (
        id: string,
        payload: Partial<WorkflowPayload>
      ): void =>
        sendOnDetails([ackKey('Workflow', 'upd', id)], mysid =>
          workflowService.updateWorkflow(id, payload, mysid)
        );

      const closeWorkflow = (): void => {
        draining.forEach(settle);
        const closing = session;
        if (closing) closing.active = store.activeWorkflow()?.active;
        session = null;
        pendingCreateToasts = [];
        patchState(store, { activeWorkflow: null, activeLoading: false });
        if (!closing) return;
        // Flush what is already saved; keep listening for acks still in flight.
        publishIfDue(closing);
        if (!hasPendingAcks(closing)) {
          closing.close$.next();
          return;
        }
        draining.add(closing);
      };

      const loadDetails = (workflowId: string): void =>
        workflowService.getDetails(workflowId, detailsMysid(workflowId));

      return {
        workflowsForItem(itemId: string): Signal<Workflow[]> {
          let cached = forItemCache.get(itemId);
          if (!cached) {
            cached = computed(() => store.workflowsByItem()[itemId] ?? []);
            forItemCache.set(itemId, cached);
          }
          return cached;
        },

        connect(itemId: string): void {
          const count = connected.get(itemId) ?? 0;
          connected.set(itemId, count + 1);
          if (count > 0) return;
          patchState(store, state => ({
            loadingByItem: { ...state.loadingByItem, [itemId]: true },
          }));
          const mysid = listMysid(itemId);
          workflowService
            .subscribe('Workflow', { mysid, view: 'plain', prms: { itemId } })
            .pipe(
              takeUntil(disconnect$.pipe(filter(id => id === itemId))),
              finalize(() => connected.delete(itemId))
            )
            .subscribe(m => onListMessage(itemId, m as WorkflowWsMessage));
          workflowService.queryWorkflows(itemId, mysid);
        },

        disconnect(itemId: string): void {
          const count = connected.get(itemId) ?? 0;
          if (count === 0) return;
          if (count > 1) {
            connected.set(itemId, count - 1);
            return;
          }
          connected.delete(itemId);
          disconnect$.next(itemId);
        },

        openWorkflow(workflowId: string): void {
          closeWorkflow();
          patchState(store, { activeLoading: true });
          const mysid = detailsMysid(workflowId);
          const s: DetailsSession = {
            id: workflowId,
            close$: new Subject<void>(),
            changed$: new Subject<void>(),
            pending: new Map(),
            publishDue: false,
            refetched: false,
            active: undefined,
          };
          session = s;
          merge(
            ...DETAILS_ENTITIES.map(ent =>
              workflowService
                .subscribe(
                  ent,
                  { mysid, view: 'details' },
                  (message: BaseWebsocketMessage) => {
                    const m = message as WorkflowWsMessage;
                    // A get reply may come back without the request's mysid.
                    return (
                      m.ent === ent &&
                      !!m.op &&
                      (m.mysid === mysid ||
                        (m.op === 'get' &&
                          (m.id === workflowId || m.res?._id === workflowId)))
                    );
                  }
                )
                .pipe(map(m => ({ ent, m: m as WorkflowWsMessage })))
            )
          )
            .pipe(takeUntil(s.close$))
            .subscribe(({ ent, m }) => onDetailsMessage(s, ent, m));
          s.changed$
            .pipe(debounceTime(300), takeUntil(s.close$))
            .subscribe(() => {
              if (!s.refetched) loadDetails(workflowId);
              s.refetched = false;
              publishIfDue(s);
            });
          loadDetails(workflowId);
        },

        closeWorkflow,
        loadDetails,

        connectMyDeals(): void {
          // The feed is shared by every dialog using it; only the first one opens it.
          myDealsCount++;
          if (myDealsCount > 1) return;
          patchState(store, { myDealsLoading: true });
          workflowService
            .subscribe('Workflow', { mysid: MY_DEALS_MYSID, view: 'myDeals' })
            .pipe(
              takeUntil(closeMyDeals$),
              finalize(() => (myDealsCount = 0))
            )
            .subscribe(message => {
              const m = message as WorkflowWsMessage;
              if (m.op === 'query') {
                patchState(
                  store,
                  isWsError(m)
                    ? {
                        myDealsLoading: false,
                        error: new Error('GNR_WORKFLOW_LOAD_FAILED'),
                      }
                    : { myDealsWorkflows: m.res ?? [], myDealsLoading: false }
                );
              }
            });
          workflowService.queryMyDeals(MY_DEALS_MYSID);
        },

        disconnectMyDeals(): void {
          if (myDealsCount === 0) return;
          myDealsCount--;
          if (myDealsCount === 0) closeMyDeals$.next();
        },

        createWorkflow(payload: { name: string; item: string }): void {
          pendingCreates.set(payload.item, [
            ...(pendingCreates.get(payload.item) ?? []),
            payload.name,
          ]);
          workflowService.createWorkflow(payload, listMysid(payload.item));
        },

        updateWorkflow,

        setActive(id: string, active: boolean): void {
          updateWorkflow(id, { active });
        },

        deleteWorkflow(id: string, itemId: string): void {
          // Always on the list feed: a delete sent on the open workflow's details
          // feed would be taken for one of its own edits and republish it.
          pendingListDeletes.add(id);
          workflowService.deleteWorkflow(id, listMysid(itemId));
          if (session?.id === id) closeWorkflow();
        },

        createTask(payload: WorkflowTaskPayload): void {
          sendOnDetails([ackKey('WorkflowTaskConfiguration', 'ins')], mysid =>
            workflowService.createTask(payload, mysid)
          );
        },

        updateTask(id: string, payload: Partial<WorkflowTaskPayload>): void {
          sendOnDetails(
            [ackKey('WorkflowTaskConfiguration', 'upd', id)],
            mysid => workflowService.updateTask(id, payload, mysid)
          );
        },

        swapTasks(a: string, b: string): void {
          sendOnDetails(
            [
              ackKey('WorkflowTaskConfiguration', 'upds'),
              ackKey('WorkflowTaskConfiguration', 'upd', a),
              ackKey('WorkflowTaskConfiguration', 'upd', b),
            ],
            mysid => workflowService.swapTasks(a, b, mysid)
          );
        },

        deleteTask(id: string): void {
          sendOnDetails(
            [ackKey('WorkflowTaskConfiguration', 'del', id)],
            mysid => workflowService.deleteTask(id, mysid)
          );
        },

        createTrigger(payload: TriggerPayload): void {
          sendOnDetails([ackKey('Trigger', 'ins')], mysid =>
            workflowService.createTrigger(payload, mysid)
          );
        },

        updateTrigger(id: string, payload: Partial<TriggerPayload>): void {
          sendOnDetails([ackKey('Trigger', 'upd', id)], mysid =>
            workflowService.updateTrigger(id, payload, mysid)
          );
        },

        deleteTrigger(id: string): void {
          sendOnDetails([ackKey('Trigger', 'del', id)], mysid =>
            workflowService.deleteTrigger(id, mysid)
          );
        },

        copyWorkflows(request: BatchCopyWorkflowsRequest): void {
          patchState(store, { copyLoading: true });
          workflowService.copyWorkflows(request).subscribe({
            next: response => {
              patchState(store, { copyLoading: false });
              if (!response?.res) {
                fail('Failed to copy workflows');
                return;
              }
              ok('Workflows copied successfully');
              store.workflowsCopied$.next();
              if (connected.has(request.targetItem))
                workflowService.queryWorkflows(
                  request.targetItem,
                  listMysid(request.targetItem)
                );
            },
            error: error => {
              patchState(store, { copyLoading: false });
              fail('Failed to copy workflows', error);
            },
          });
        },

        loadRoleParticipants: rxMethod<{ itemId: string; roles: string[] }>(
          pipe(
            filter(({ roles }) => roles.length > 0),
            groupBy(({ itemId }) => itemId),
            mergeMap(group$ =>
              group$.pipe(
                switchMap(({ itemId, roles }) =>
                  workflowService.getRoleParticipants(itemId, roles).pipe(
                    map(({ res }) => res ?? EMPTY_ROLE_PARTICIPANTS),
                    catchError((err: HttpErrorResponse) => {
                      if (!isMissingParticipation(err))
                        patchState(store, { error: err });
                      return of(EMPTY_ROLE_PARTICIPANTS);
                    }),
                    tap(data =>
                      patchState(store, state => ({
                        roleParticipantsByItem: {
                          ...state.roleParticipantsByItem,
                          [itemId]: data,
                        },
                      }))
                    )
                  )
                )
              )
            )
          )
        ),

        clearRoleParticipants(itemId: string): void {
          patchState(store, state => {
            const roleParticipantsByItem = { ...state.roleParticipantsByItem };
            delete roleParticipantsByItem[itemId];
            return { roleParticipantsByItem };
          });
        },

        loadEmailDynamicContent: rxMethod<string | undefined>(
          pipe(
            switchMap(taskId =>
              workflowService.getEmailDynamicContent(taskId).pipe(
                tap(({ res }) =>
                  patchState(store, { emailDynamicContent: res ?? null })
                ),
                catchError((error: HttpErrorResponse) => {
                  patchState(store, { error });
                  return EMPTY;
                })
              )
            )
          )
        ),

        loadEmailTemplates(): void {
          workflowService.getEmailTemplates().subscribe({
            next: ({ res }) => patchState(store, { emailTemplates: res ?? [] }),
            error: (error: HttpErrorResponse) => patchState(store, { error }),
          });
        },

        startWorkflow(id: string, itemId: string): void {
          const active = store.activeWorkflow();
          const open = active?._id === id;
          const edited = session?.id === id ? session : null;
          const needsPublish =
            !open ||
            !active.flowableProcessDefinitionKey ||
            !!edited?.publishDue ||
            (!!edited && hasPendingAcks(edited));
          if (edited) edited.publishDue = false;
          (needsPublish
            ? workflowFlowableService
                .publish(id)
                .pipe(tap(r => patchPublished(id, r)))
            : of(null)
          )
            .pipe(switchMap(() => workflowFlowableService.start(id, itemId)))
            .subscribe({
              next: () => ok('GNR_WORKFLOW_TRIGGERED_SUCCESSFULLY'),
              error: error => fail('GNR_WORKFLOW_TRIGGER_FAILED', error),
            });
        },

        workflowRunsByWorkflowId(workflowId: string): Signal<WorkflowRun[]> {
          return computed(() => store.workflowRuns()[workflowId] ?? []);
        },

        workflowRunsLoadingByWorkflowId(workflowId: string): Signal<boolean> {
          return computed(
            () => store.workflowRunsLoading()[workflowId] ?? false
          );
        },

        workflowHistoryByRunId(
          runId: string
        ): Signal<WorkflowHistoryActivity[] | null> {
          return computed(() => store.workflowHistory()[runId] ?? null);
        },

        workflowHistoryLoadingByRunId(runId: string): Signal<boolean> {
          return computed(() => store.workflowHistoryLoading()[runId] ?? false);
        },

        subscribeWorkflowRuns(workflowId: string): void {
          const count = runsCount.get(workflowId) ?? 0;
          runsCount.set(workflowId, count + 1);
          if (count > 0) return;
          patchState(store, state => ({
            workflowRunsLoading: {
              ...state.workflowRunsLoading,
              [workflowId]: true,
            },
          }));
          runService
            .subscribeRuns(workflowId, `wf-${workflowId}-runs`)
            .pipe(
              takeUntil(disconnectRuns$.pipe(filter(id => id === workflowId))),
              finalize(() => runsCount.delete(workflowId))
            )
            .subscribe(m => onRunsMessage(workflowId, m));
        },

        unsubscribeWorkflowRuns(workflowId: string): void {
          const count = runsCount.get(workflowId) ?? 0;
          if (count === 0) return;
          if (count > 1) {
            runsCount.set(workflowId, count - 1);
            return;
          }
          disconnectRuns$.next(workflowId);
          patchState(store, state => {
            const { [workflowId]: _r, ...workflowRuns } = state.workflowRuns;
            const { [workflowId]: _rl, ...workflowRunsLoading } =
              state.workflowRunsLoading;
            return { workflowRuns, workflowRunsLoading };
          });
        },

        subscribeWorkflowHistory(runId: string): void {
          const count = historyCount.get(runId) ?? 0;
          historyCount.set(runId, count + 1);
          if (count > 0) return;
          patchState(store, state => ({
            workflowHistory: {
              ...state.workflowHistory,
              [runId]: null,
            } as Record<string, WorkflowHistoryActivity[] | null>,
            workflowHistoryLoading: {
              ...state.workflowHistoryLoading,
              [runId]: true,
            },
          }));
          runService
            .subscribeHistory(runId, `wf-${runId}-history`)
            .pipe(
              takeUntil(disconnectHistory$.pipe(filter(id => id === runId))),
              finalize(() => historyCount.delete(runId))
            )
            .subscribe(m => onHistoryMessage(runId, m));
        },

        unsubscribeWorkflowHistory(runId: string): void {
          const count = historyCount.get(runId) ?? 0;
          if (count === 0) return;
          if (count > 1) {
            historyCount.set(runId, count - 1);
            return;
          }
          disconnectHistory$.next(runId);
          patchState(store, state => {
            const { [runId]: _h, ...workflowHistory } = state.workflowHistory;
            const { [runId]: _hl, ...workflowHistoryLoading } =
              state.workflowHistoryLoading;
            return { workflowHistory, workflowHistoryLoading };
          });
        },
      };
    }
  )
);
